import React, { useState, useEffect } from "react";
import { Location as TQSLocation } from "../../models/location";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import { format } from "date-fns";
export default function UpcomingDates(props: {
  selectedLocation: TQSLocation;
  allLocations: TQSLocation[];
  isLoading: boolean;
}): JSX.Element {
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [sortAscending, setSortAscending] = useState(true);

  useEffect(() => {
    const selectedIndex = props.allLocations.findIndex(
      (location) => location.name === props.selectedLocation.name,
    );
    if (selectedIndex !== -1) {
      setOpenDropdown(selectedIndex);
    } else {
      setOpenDropdown(null);
    }
  }, [props.selectedLocation, props.allLocations]);

  const toggleDropdown = (index: number) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const toggleSort = () => {
    setSortAscending(!sortAscending);
  };

  const formatDate = (dateString: string) => {
    // Assuming dateString is already in "yyyy-MM-dd" format or similar
    const dateParts = dateString.split(/[-T]/); // Split by '-' or 'T'

    // Check if the split resulted in exactly 3 parts and all are numeric
    if (
      dateParts.length !== 3 ||
      isNaN(Number(dateParts[0])) ||
      isNaN(Number(dateParts[1])) ||
      isNaN(Number(dateParts[2]))
    ) {
      return "Invalid Date";
    }

    // Format as "yyyy/MM/dd"
    return `${dateParts[0]}/${dateParts[1]}/${dateParts[2]}`;
  };

  const sortDates = (dates: any[], ascending: boolean) => {
    return dates
      .slice()
      .sort(
        (
          a: { startTime: string | number | Date },
          b: { startTime: string | number | Date },
        ) => {
          const dateA = new Date(a.startTime);
          const dateB = new Date(b.startTime);
          return ascending
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        },
      );
  };

  return (
    <div className="flex h-[100%] min-h-[1px] w-full flex-col overflow-auto rounded-lg border border-[#E5E5E5] bg-white">
      <div className="ml-4 flex flex-row items-center gap-x-[8px] pt-2">
        <FontAwesomeIcon className="text-[18px]" icon={faCalendar} />
        <span className="text-[20px] font-semibold">Upcoming Dates</span>
      </div>
      <div className="flex flex-col overflow-y-auto p-2">
        {props.allLocations?.length === 0 ? (
          <div className="flex h-[500px] items-center justify-center">
            <span className="loading loading-spinner loading-lg text-tqsYellow"></span>
          </div>
        ) : (
          (props.selectedLocation && "id" in props.selectedLocation
            ? [props.selectedLocation] // If selectedLocation has an id, show only the selected location
            : props.allLocations
          ).map((location, index) => (
            <div key={index} className="mb-1 rounded-lg last:mb-0">
              <button
                className="flex w-full flex-row items-center rounded-[4px] border border-gray-300 text-left focus:outline-none justify-between hover:bg-[#FAFAFA] group"
                onClick={() => toggleDropdown(index)}
              >
                <div className="flex w-[calc(100%-50px)] flex-row items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="ml-2 size-6 text-black group-hover:text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d={
                        openDropdown === index
                          ? "M4.5 15.75l7.5-7.5 7.5 7.5"
                          : "M19.5 8.25l-7.5 7.5-7.5-7.5"
                      }
                    />
                  </svg>
                  <span className="m-2 w-full overflow-hidden text-ellipsis whitespace-nowrap text-[14px] font-semibold text-black group-hover:text-gray-400">
                    {location.name}
                  </span>
                </div>
                <div className="mr-2 flex text-[10px] w-[50px] text-gray-400">
                  <span className="opacity-0 hover:opacity-100 group-hover:opacity-100">{openDropdown === index ? 'collapse' : 'expand'}</span>
                </div>
              </button>
              {openDropdown === index && (
                <div className="dropdown-content overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="px-2 py-1 text-[11px] text-gray-400 pl-[40px]">
                          Date period
                          <button onClick={toggleSort} className="ml-2">
                            {sortAscending ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="ml-1 inline h-4 w-4"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="ml-1 inline h-4 w-4 rotate-180"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            )}
                          </button>
                        </th>
                        <th className="px-2 text-[10px] font-semibold text-gray-400">
                          Type
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortDates(
                        [
                          ...location.weekendProduction.map((w) => {
                            return { ...w, type: "Weekend Production" };
                          }),
                          ...location.plantShutdowns.map((s) => {
                            return { ...s, type: "Shutdown" };
                          }),
                        ].filter((w) => {
                          return (
                            w.endTime >= new Date().toISOString().split("T")[0]
                          );
                        }),
                        sortAscending,
                      ).map(
                        (
                          date: {
                            reason:
                              | string
                              | number
                              | boolean
                              | React.ReactElement<
                                  any,
                                  string | React.JSXElementConstructor<any>
                                >
                              | Iterable<React.ReactNode>
                              | null
                              | undefined;
                            startTime: string;
                            endTime: string;
                            type: string;
                          },
                          dateIndex: React.Key | null | undefined,
                        ) => (
                          <tr key={dateIndex}>
                            <td className="mr-2 w-1/2 px-2 py-1 text-xs text-black">
                              <div className="flex items-center">
                                {date.type === "Shutdown" ? (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="mr-2 h-6 max-h-6 min-h-6 w-6 min-w-6 max-w-6 rounded-full border border-tqsRed bg-tqsLightRed text-tqsRed"
                                    style={{ padding: "4px" }}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="tqs-yellow mr-2 h-6 max-h-6 min-h-6 w-6 min-w-6 max-w-6 rounded-full border border-tqsYellow bg-tqsLighterYellow text-tqsYellow"
                                    style={{ padding: "4px" }}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                                    />
                                  </svg>
                                )}
                                <div className="flex flex-col">
                                  <div className="text-[10px]">
                                    Start: {formatDate(date.startTime)}
                                  </div>
                                  <div className="text-[10px]">
                                    End: {formatDate(date.endTime)}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td
                              className={`px-2 py-1 text-[10px] font-semibold ${
                                date.type === "Shutdown"
                                  ? "text-tqsRed"
                                  : "text-tqsYellow"
                              } w-full`}
                            >
                              {date.type}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>

                  {[
                    ...location.weekendProduction,
                    ...location.plantShutdowns,
                  ].filter((w) => {
                    return w.endTime >= new Date().toISOString().split("T")[0];
                  }).length <= 0 && (
                    <div className="flex w-full items-center justify-center pb-[8px] pt-[8px]">
                      <span className="text-[11px]">No upcoming dates</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
